import { Box, Flex } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import {
	useGetNotificationsSettings,
	useUpdateBulkNotificationsSettings,
} from '@/hooks/service-hooks/notifications';
import type {
	NotificationType as APINotificationType,
	NotificationSettings,
} from '@/services/notifications';
import { useAuthStore } from '@/store/authStore';
// import NotificationMedium from './NotificationMedium';
import NotificationType from './NotificationType';
import { useNotificationContext } from '@/components/ui/notification-provider';
import Show from '@/components/Molecules/Show';
import FaceliftText from '@/components/Atoms/FaceliftText';

export interface NotificationMethod {
	name: string;
	enabled: boolean;
}

export interface NotificationItem {
	module: string;
	enabled: boolean;
	methods: NotificationMethod[];
}

const Notifications = () => {
	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;
  const { isEnabled: notificationsEnabled } = useNotificationContext();

	const { data: settingsData, isLoading: isLoadingSettings } =
		useGetNotificationsSettings(userId);

		// const [notificationMedium, setNotificationMedium] = useState<
		// 	NotificationItem[]
		// >([]);
	const [notificationTypes, setNotificationTypes] = useState<
		NotificationItem[]
	>([]);
	const [originalSettings, setOriginalSettings] =
		useState<NotificationSettings | null>(null);
	const [isUpdating, setIsUpdating] = useState(false);

	useEffect(() => {
		if (settingsData?.data) {
			const settings = settingsData.data;
			setOriginalSettings(settings);

			// if (settings.medium) {
			// 	const mediumItems: NotificationItem[] = settings.medium.map((item) => ({
			// 		module: item.module,
			// 		enabled: item.enabled,
			// 		methods: [],
			// 	}));
			// 	setNotificationMedium(mediumItems);
			// }

			if (settings.types) {
				const typeItems: NotificationItem[] = settings.types.map((item) => ({
					module: item.module,
					enabled: item.enabled,
					methods: item.methods?.map((method) => ({
						name: method.name,
						enabled: method.enabled,
					})),
				}));
				setNotificationTypes(typeItems);
			}
		}
	}, [settingsData]);

	// const handleMediumSwitchChange = (index: number, value: boolean) => {
	// 	setNotificationMedium((prev) =>
	// 		prev.map((item, i) => (i === index ? { ...item, enabled: value } : item)),
	// 	);
	// };

	const handleTypeSwitchChange = (index: number, value: boolean) => {
		setNotificationTypes((prev) =>
			prev.map((item, i) => (i === index ? { ...item, enabled: value } : item)),
		);
	};

	const handleTypeChange = (
		itemIndex: number,
		methodIndex: number,
		value: boolean,
	) => {
		setNotificationTypes((prev) =>
			prev.map((item, i) =>
				i === itemIndex
					? {
							...item,
							methods: item.methods.map((method, j) =>
								j === methodIndex ? { ...method, enabled: value } : method,
							),
						}
					: item,
			),
		);
	};

	const bulkUpdateMutation = useUpdateBulkNotificationsSettings(
		userId as string,
		() => {
			toast.success('Notification settings updated successfully');
		},
		(error) => {
			toast.error(error as string);
		},
	);

	const handleUpdate = async () => {
		if (!userId || !originalSettings) {
			toast.error('User ID not found');
			return;
		}

		setIsUpdating(true);

		try {
			const updatedTypes: APINotificationType[] = originalSettings.types.map(
				(originalType) => {
					const currentType = notificationTypes.find(
						(t) => t.module === originalType.module,
					);

					return {
						module: originalType.module,
						entityName: originalType.entityName,
						enabled: currentType?.enabled ?? originalType.enabled,
						methods: originalType.methods.map((originalMethod) => {
							const currentMethod = currentType?.methods.find(
								(m) => m.name === originalMethod.name,
							);

							return {
								name: originalMethod.name,
								action: originalMethod.action,
								enabled: currentMethod?.enabled ?? originalMethod.enabled,
							};
						}),
					};
				},
			);

			await bulkUpdateMutation.mutateAsync({
				types: updatedTypes,
			});
		} catch (error) {
			toast.error(error as string);
		} finally {
			setIsUpdating(false);
		}
	};

	return (
		<Box width="100%">
			<Flex direction="column" gap={8}>
				{/* <NotificationMedium
					items={notificationMedium}
					onSwitchChange={handleMediumSwitchChange}
				/> */}
				<Show when={notificationsEnabled}>
					<NotificationType
						items={notificationTypes}
						onSwitchChange={handleTypeSwitchChange}
						onMethodChange={handleTypeChange}
					/>

					<Flex justify="flex-end" mt={4}>
						<FaceliftButton
							width="250px"
							size="md"
							onClick={handleUpdate}
							loading={isUpdating || isLoadingSettings}
							disabled={isUpdating || isLoadingSettings}
						>
							Update
						</FaceliftButton>
					</Flex>
				</Show>
				<Show when={!notificationsEnabled}>
					<Box textAlign="center" py={8}>
						<FaceliftText fontWeight="bold" fontSize="md" mb={4}>
							Please contact the administrator to enable notifications.
						</FaceliftText>
					</Box>
				</Show>
			</Flex>
		</Box>
	);
};

export default Notifications;
